import { type FormEvent, useState } from 'react';
import { ApiError, apiSend, type Project } from '../lib/api';
import { Alert, Button, cardClass, fieldClass } from './ui';

type Sort = 'asc' | 'desc';
type Format = 'number' | 'time';
type Reset = 'none' | 'daily' | 'weekly' | 'monthly';

/** A board as the Leaderboards tab knows it. */
export interface BoardSettings {
  id: string;
  name: string;
  sort: Sort;
  format: Format;
  reset: Reset;
  min: number | null;
  max: number | null;
  hasScores: boolean;
}

/** `Fastest lap` → `fastest-lap`: the id a game uses, made from the name. */
export function boardIdFrom(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

function Choices<T extends string>({
  legend,
  name,
  value,
  options,
  locked,
  onChange,
}: {
  legend: string;
  name: string;
  value: T;
  options: [T, string, string?][];
  locked: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-[13px] font-semibold text-muted">{legend}</legend>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
        {options.map(([option, label, hint]) => (
          <label
            key={option}
            className={`flex min-h-11 items-center gap-2.5 rounded-[6px] border px-3.5 font-semibold ${
              value === option ? 'border-brand bg-cream' : 'border-line bg-field'
            } ${locked ? 'opacity-60' : ''}`}
          >
            <input
              type="radio"
              name={name}
              checked={value === option}
              disabled={locked}
              onChange={() => onChange(option)}
            />
            {label}
            {hint && <span className="font-medium text-muted">· {hint}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const asLimit = (text: string): number | null | 'bad' => {
  if (!text.trim()) return null;
  const value = Number(text.replace(/,/g, ''));
  return Number.isFinite(value) ? value : 'bad';
};

/**
 * Making or editing a leaderboard (screens 10–11 of the approved mockup). Test gets it right
 * away; once it has scores, its order, format and reset lock.
 */
export function BoardForm({
  project,
  board,
  onDone,
  onCancel,
}: {
  project: Project;
  board?: BoardSettings;
  onDone: (id: string | null) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(board?.name ?? '');
  const [id, setId] = useState(board?.id ?? '');
  const [idEdited, setIdEdited] = useState(false);
  const [sort, setSort] = useState<Sort>(board?.sort ?? 'desc');
  const [format, setFormat] = useState<Format>(board?.format ?? 'number');
  const [reset, setReset] = useState<Reset>(board?.reset ?? 'none');
  const [min, setMin] = useState(board?.min === null || !board ? '' : String(board.min));
  const [max, setMax] = useState(board?.max === null || !board ? '' : String(board.max));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const locked = Boolean(board?.hasScores);
  const base = `/v1/dev/projects/${project.id}/leaderboards`;

  async function save(event: FormEvent) {
    event.preventDefault();
    const low = asLimit(min);
    const high = asLimit(max);
    if (low === 'bad' || high === 'bad') return setError('Limits are numbers, or empty for none.');
    const settings = { name: name.trim(), sort, format, reset, min: low, max: high };
    setBusy(true);
    setError(null);
    try {
      if (board) await apiSend('PATCH', `${base}/${board.id}`, settings);
      else await apiSend('POST', base, { id, ...settings });
      onDone(board?.id ?? id);
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  async function remove() {
    if (!board) return;
    setBusy(true);
    setError(null);
    try {
      await apiSend('DELETE', `${base}/${board.id}`);
      onDone(null);
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-5">
      <div className="flex min-w-0 flex-[999_1_520px] flex-col gap-5">
        <form
          onSubmit={save}
          aria-labelledby="board-form-heading"
          className={`${cardClass} flex flex-col gap-5 p-6`}
        >
          <div>
            <h2 id="board-form-heading" className="text-[22px] font-semibold">
              {board ? `Edit ${board.name}` : 'New leaderboard'}
            </h2>
            <p className="mt-1.5 text-muted">
              {board ? (
                <>
                  <span className="font-mono">{board.id}</span>
                  {board.hasScores ? ' · has scores in test' : ''}
                </>
              ) : (
                'Your test community gets it right away. Live players get it when you publish a version.'
              )}
            </p>
          </div>
          {error && <Alert tone="error">{error}</Alert>}

          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Name players see
              <input
                value={name}
                maxLength={60}
                onChange={(event) => {
                  setName(event.target.value);
                  if (!board && !idEdited) setId(boardIdFrom(event.target.value));
                }}
                className={fieldClass}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Id your game uses
              <input
                value={id}
                readOnly={Boolean(board)}
                onChange={(event) => {
                  setId(event.target.value);
                  setIdEdited(true);
                }}
                className={`${fieldClass} font-mono`}
              />
              <span className="font-medium">
                {board ? 'Fixed once made.' : 'Made from the name. Fixed once created.'}
              </span>
            </label>
          </div>

          {locked && (
            <p className="rounded-[6px] border border-dashed border-line px-4 py-3 text-sm text-muted">
              This board has scores, so its order, format and reset are locked: changing them would
              make today’s scores mean something else. To change them, make a new board and point
              your game at it.
            </p>
          )}
          <Choices
            legend="Best score is"
            name="sort"
            value={sort}
            locked={locked}
            onChange={setSort}
            options={[
              ['desc', 'Highest first', 'points, coins'],
              ['asc', 'Lowest first', 'times, moves'],
            ]}
          />
          <Choices
            legend="Show scores as"
            name="format"
            value={format}
            locked={locked}
            onChange={setFormat}
            options={[
              ['number', 'A number', '12,850'],
              ['time', 'A time', '1:02.345'],
            ]}
          />
          {format === 'time' && (
            <p className="-mt-3 text-[13px] text-muted">
              For a time, your game sends milliseconds: 62345 shows as 1:02.345.
            </p>
          )}
          <Choices
            legend="Starts fresh"
            name="reset"
            value={reset}
            locked={locked}
            onChange={setReset}
            options={[
              ['none', 'Never'],
              ['daily', 'Every day'],
              ['weekly', 'Every week'],
              ['monthly', 'Every month'],
            ]}
          />
          <p className="-mt-3 text-[13px] text-muted">
            At 00:00 UTC; weeks start on Monday. Past days, weeks and months stay to look back on.
          </p>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Lowest allowed score (optional)
              <input
                value={min}
                inputMode="decimal"
                placeholder="No limit"
                onChange={(event) => setMin(event.target.value)}
                className={fieldClass}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Highest allowed score (optional)
              <input
                value={max}
                inputMode="decimal"
                placeholder="No limit"
                onChange={(event) => setMax(event.target.value)}
                className={fieldClass}
              />
            </label>
          </div>
          <p className="-mt-3 text-[13px] text-muted">
            MOGZO refuses scores outside these, so a cheat or a bug can’t top the board.
            {board ? ' New limits apply to scores sent from now on.' : ''}
          </p>

          <div className="flex flex-wrap gap-2.5">
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : board ? 'Save changes' : 'Create leaderboard'}
            </Button>
            <Button variant="secondary" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>

        {board && (
          <section
            aria-labelledby="delete-board-heading"
            className={`${cardClass} flex flex-col gap-3 border-danger px-6 py-5`}
          >
            <h2 id="delete-board-heading" className="text-[15px] font-semibold">
              Delete this leaderboard
            </h2>
            {confirmDelete ? (
              <div className="flex flex-col gap-2.5 rounded-[6px] bg-danger-soft px-4 py-3.5">
                <strong className="text-danger">Delete {board.name} from test?</strong>
                <p className="text-muted">
                  Scores sent to <span className="font-mono">{board.id}</span> are refused from now
                  on, so take it out of your game too. Its test scores are deleted. Live players
                  keep it until you publish a version without it.
                </p>
                <div className="flex flex-wrap gap-2.5">
                  <button
                    type="button"
                    onClick={remove}
                    disabled={busy}
                    className="mz-btn mz-btn-danger"
                  >
                    Yes, delete it
                  </button>
                  <Button
                    variant="secondary"
                    onClick={() => setConfirmDelete(false)}
                    disabled={busy}
                  >
                    Keep it
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                  Delete leaderboard
                </Button>
              </div>
            )}
          </section>
        )}
      </div>

      <aside
        aria-labelledby="board-code-heading"
        className={`${cardClass} flex max-w-[360px] flex-[1_1_280px] flex-col gap-3 p-5`}
      >
        <h2 id="board-code-heading" className="text-[15px] font-semibold">
          In your game
        </h2>
        <pre className="rounded-[6px] border border-line bg-field px-3.5 py-3 font-mono text-[13px] leading-relaxed whitespace-pre-wrap">
          {`await mogzo.leaderboards.submit(\n  '${id || 'board-id'}', score);\n\nconst { entries } = await\n  mogzo.leaderboards.top('${id || 'board-id'}');`}
        </pre>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-muted">
          <li>Send one score at the end of a run. Each player’s best counts.</li>
          <li>Once the board has scores, its order, format and reset lock.</li>
        </ul>
      </aside>
    </div>
  );
}
